import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  VStack,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  Text,
  Checkbox,
  CheckboxGroup,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  SimpleGrid,
  HStack,
} from '@chakra-ui/react';
import rolePermissionService from 'services/rolePermissionService';

export default function RoleFormModal({ isOpen, onClose, role, isEditing, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
  });
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      loadPermissions();
      
      if (isEditing && role) {
        setFormData({
          name: role.name || '',
          description: role.description || '',
          isActive: role.isActive !== undefined ? role.isActive : true,
        });
        loadRolePermissions(role.id);
      } else {
        setFormData({
          name: '',
          description: '',
          isActive: true,
        });
        setSelectedPermissions([]);
      }
    }
  }, [isOpen, isEditing, role]);

  const loadPermissions = async () => {
    try {
      setLoadingPermissions(true);
      const response = await rolePermissionService.getPermissions();
      setPermissions(response.payload?.permissions || []);
    } catch (error) {
      console.error('Error loading permissions:', error);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const loadRolePermissions = async (roleId) => {
    try {
      const response = await rolePermissionService.getRolePermissions(roleId);
      const currentPermissions = response.payload?.permissions || [];
      setSelectedPermissions(currentPermissions.map(p => String(p.id)));
    } catch (error) {
      console.error('Error loading role permissions:', error);
    }
  };

  const handlePermissionChange = (permissionIds) => {
    setSelectedPermissions(permissionIds);
  };

  const handleIndividualPermissionChange = (permissionId, isChecked) => {
    const permissionIdStr = String(permissionId);
    setSelectedPermissions(prev => {
      if (isChecked) {
        return [...prev, permissionIdStr];
      } else {
        return prev.filter(id => id !== permissionIdStr);
      }
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Error',
        description: 'El nombre del rol es requerido',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      
      let roleId;
      if (isEditing) {
        await rolePermissionService.updateRole(role.id, formData);
        roleId = role.id;
      } else {
        const response = await rolePermissionService.createRole(formData);
        roleId = response.payload?.role?.id;
      }
      
      // Asignar permisos si se seleccionaron
      if (roleId && selectedPermissions.length > 0) {
        const permissionIds = selectedPermissions.map(id => parseInt(id, 10));
        await rolePermissionService.assignPermissionsToRole(roleId, permissionIds);
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving role:', error);
      toast({
        title: 'Error',
        description: `Error al ${isEditing ? 'actualizar' : 'crear'} el rol`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      isActive: true,
    });
    setSelectedPermissions([]);
    onClose();
  };

  // Agrupar permisos por recurso
  const groupedPermissions = permissions.reduce((groups, permission) => {
    const resource = permission.resource;
    if (!groups[resource]) {
      groups[resource] = [];
    }
    groups[resource].push(permission);
    return groups;
  }, {});

  const resourceLabels = {
    users: 'Usuarios',
    roles: 'Roles',
    clients: 'Clientes',
    vehicles: 'Vehículos',
    services: 'Servicios',
    schedule: 'Agenda',
    reports: 'Reportes',
    inventory: 'Inventario',
    settings: 'Configuración',
    invoices: 'Facturas',
    providers: 'Proveedores',
    products: 'Productos',
    permissions: 'Permisos'
  };

  const actionLabels = {
    view: 'Ver',
    create: 'Crear',
    edit: 'Editar',
    delete: 'Eliminar',
    assign: 'Asignar',
    view_own: 'Ver Propio',
    generate: 'Generar',
    manage: 'Gestionar'
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent maxW="900px">
        <ModalHeader>
          {isEditing ? 'Editar Rol' : 'Crear Nuevo Rol'}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <Tabs>
            <TabList>
              <Tab>Información Básica</Tab>
              <Tab>
                Permisos 
                {selectedPermissions.length > 0 && (
                  <Badge ml={2} colorScheme="gray">{selectedPermissions.length}</Badge>
                )}
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Nombre del Rol</FormLabel>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Ej: Administrador, Mecánico, etc."
                      disabled={isEditing && role?.name === 'Admin'}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Descripción</FormLabel>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Describe las responsabilidades de este rol..."
                      rows={3}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="role-active" mb="0">
                      Rol Activo
                    </FormLabel>
                    <Switch
                      id="role-active"
                      isChecked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    />
                  </FormControl>
                </VStack>
              </TabPanel>

              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between" align="center">
                    <Text fontSize="sm" color="gray.600">
                      Selecciona los permisos que tendrá este rol
                    </Text>
                    <Badge colorScheme="gray">
                      {selectedPermissions.length} de {permissions.length} seleccionados
                    </Badge>
                  </HStack>

                  {loadingPermissions ? (
                    <Text>Cargando permisos...</Text>
                  ) : (
                    <Box maxH="400px" overflowY="auto">
                      <CheckboxGroup
                        value={selectedPermissions}
                        onChange={handlePermissionChange}
                      >
                        <Accordion allowMultiple defaultIndex={[0]}>
                          {Object.entries(groupedPermissions).map(([resource, resourcePermissions]) => (
                            <AccordionItem key={resource}>
                              <h2>
                                <AccordionButton>
                                  <Box flex="1" textAlign="left">
                                    <HStack>
                                      <Text fontWeight="semibold">
                                        {resourceLabels[resource] || resource}
                                      </Text>
                                      <Badge size="sm" colorScheme="gray">
                                        {resourcePermissions.filter(p => 
                                          selectedPermissions.includes(String(p.id))
                                        ).length} / {resourcePermissions.length}
                                      </Badge>
                                    </HStack>
                                  </Box>
                                  <AccordionIcon />
                                </AccordionButton>
                              </h2>
                              <AccordionPanel pb={4}>
                                <SimpleGrid columns={2} spacing={3}>
                                  {resourcePermissions.map((permission) => (
                                    <Checkbox
                                      key={permission.id}
                                      value={String(permission.id)}
                                      isChecked={selectedPermissions.includes(String(permission.id))}
                                      onChange={(e) => handleIndividualPermissionChange(permission.id, e.target.checked)}
                                    >
                                      <VStack align="start" spacing={0}>
                                        <Text fontSize="sm" fontWeight="medium">
                                          {actionLabels[permission.action] || permission.action}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                          {permission.name}
                                        </Text>
                                      </VStack>
                                    </Checkbox>
                                  ))}
                                </SimpleGrid>
                              </AccordionPanel>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      </CheckboxGroup>
                    </Box>
                  )}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="gray"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText={isEditing ? 'Actualizando...' : 'Creando...'}
          >
            {isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}